import { useAppTheme } from "@hooks/useAppTheme";
import { Box, useDevice } from "@memorang/ui";
import ContentLoader from "react-native-easy-content-loader";
import { Card } from "react-native-paper";

const flatContainer = { paddingHorizontal: 0, width: "100%" as const };
const flatAvatar = { marginRight: 0 };

const ModuleRowSkeleton = () => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	return (
		<Card
			mode="outlined"
			style={{
				borderRadius: theme.roundness,
				overflow: "hidden",
				backgroundColor: theme.colors.surface,
				borderColor: theme.dark
					? theme.colors.outline
					: theme.colors.outlineVariant,
				marginHorizontal: isMobile ? 16 : 0,
			}}
		>
			<Box
				flexDirection="row"
				alignItems="center"
				padding={12}
				style={{ gap: 12 }}
			>
				{/* Thumbnail */}
				{/*@ts-ignore*/}
				<ContentLoader
					avatar
					aSize={72}
					aShape="square"
					title={false}
					pRows={0}
					avatarStyles={flatAvatar}
					containerStyles={{ paddingHorizontal: 0, width: 72 }}
				/>
				{/* Title + subtitle + chip (real: gap 4, chip marginTop 8) */}
				<Box flex={1} style={{ gap: 4 }}>
					{/*@ts-ignore*/}
					<ContentLoader
						title={false}
						pRows={2}
						pHeight={[14, 12]}
						pWidth={["55%", "85%"]}
						paragraphStyles={{ borderRadius: 4, marginVertical: 5 }}
						containerStyles={flatContainer}
					/>
					{/*@ts-ignore*/}
					<ContentLoader
						title={false}
						pRows={1}
						pHeight={20}
						pWidth={64}
						paragraphStyles={{ borderRadius: 6, marginTop: 8, marginBottom: 0 }}
						containerStyles={flatContainer}
					/>
				</Box>
				{/* Trailing action (real: paddingLeft 4) */}
				<Box justifyContent="center" paddingLeft={4}>
					{/*@ts-ignore*/}
					<ContentLoader
						title={false}
						pRows={1}
						pHeight={34}
						pWidth={90}
						paragraphStyles={{ borderRadius: 22, marginVertical: 0 }}
						containerStyles={{ paddingHorizontal: 0, width: 90 }}
					/>
				</Box>
			</Box>
		</Card>
	);
};

const MODULE_GRID_CARD_HEIGHT = 168;

const ModuleGridSkeleton = () => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	return (
		<Card
			mode="outlined"
			style={{
				borderRadius: theme.roundness,
				backgroundColor: theme.colors.surface,
				borderColor: theme.dark
					? theme.colors.outline
					: theme.colors.outlineVariant,
				overflow: "hidden",
				...(isMobile ? {} : { height: MODULE_GRID_CARD_HEIGHT }),
			}}
		>
			<Box
				padding={16}
				flex={1}
				justifyContent="space-between"
				style={{
					gap: 12,
					...(isMobile ? {} : { height: MODULE_GRID_CARD_HEIGHT }),
				}}
			>
				<Box flexDirection="row" gap={12} alignItems="flex-start" flex={1}>
					{/*@ts-ignore*/}
					<ContentLoader
						avatar
						aSize={64}
						aShape="square"
						title={false}
						pRows={0}
						avatarStyles={flatAvatar}
						containerStyles={{ paddingHorizontal: 0, width: 64 }}
					/>
					<Box flex={1} style={{ gap: 4 }}>
						{/*@ts-ignore*/}
						<ContentLoader
							title={false}
							pRows={2}
							pHeight={12}
							pWidth={["70%", "90%"]}
							paragraphStyles={{ borderRadius: 4, marginVertical: 3 }}
							containerStyles={flatContainer}
						/>
					</Box>
				</Box>
				<Box
					flexDirection="row"
					alignItems="flex-end"
					justifyContent="space-between"
					gap={12}
				>
					{/*@ts-ignore*/}
					<ContentLoader
						title={false}
						pRows={1}
						pHeight={20}
						pWidth={64}
						paragraphStyles={{ borderRadius: 6, marginVertical: 0 }}
						containerStyles={{ paddingHorizontal: 0, width: 64 }}
					/>
					{/*@ts-ignore*/}
					<ContentLoader
						title={false}
						pRows={1}
						pHeight={34}
						pWidth={90}
						paragraphStyles={{ borderRadius: 22, marginVertical: 0 }}
						containerStyles={{ paddingHorizontal: 0, width: 90 }}
					/>
				</Box>
			</Box>
		</Card>
	);
};

export const ModulesListSkeleton = ({
	viewMode = "list",
	count = 5,
}: {
	viewMode?: "list" | "grid";
	count?: number;
}) => {
	const { isMobile } = useDevice();

	if (viewMode === "grid") {
		const columnCount = isMobile ? 1 : 2;
		const rows = Math.ceil(count / columnCount);
		return (
			<Box style={{ gap: 16 }}>
				{[...new Array(rows).keys()].map((rowIndex) => (
					<Box
						key={rowIndex}
						flexDirection={isMobile ? "column" : "row"}
						style={{ gap: 16 }}
					>
						{[...new Array(columnCount).keys()].map((colIndex) => (
							<Box key={colIndex} flex={1} minWidth={0}>
								<ModuleGridSkeleton />
							</Box>
						))}
					</Box>
				))}
			</Box>
		);
	}

	return (
		<Box style={{ gap: 12 }}>
			{[...new Array(count).keys()].map((item) => (
				<ModuleRowSkeleton key={item} />
			))}
		</Box>
	);
};

export default ModulesListSkeleton;
